import { FieldType, MeasurementUnits } from '../../../shared/enums';

import * as _ from 'lodash';
import { RequiredFieldValidator } from '../../../shared/zoneiqui/field-validators/required-field.validator';
import { IFieldSettings, IFieldValidator } from 'src/app/interfaces/field-settings.interface';
import { IBaseTable } from 'src/app/interfaces/calibration-set-table.interface';
import { NA_VALUE } from 'src/app/constants';
import { IFieldValidationParams } from 'src/app/interfaces/zc-validation-result.interface';

type DisabledFunction = (model: any) => boolean;

interface IParameters {
    validators?: IFieldValidator[];
    isRequired?: boolean;
    isRelatedToRequired?: boolean;
    section?: any;
    isLongMeasurementSection?: boolean;
    disabledFunction?: DisabledFunction;
    showFieldLevelNAButton?: boolean;
    jointValidationFieldsList?: string[];
}

export class FieldSettingsModel implements IFieldSettings {
    name: string;
    label: string;
    type: FieldType;
    measurementUnits: MeasurementUnits;
    section?: number;
    isRequired?: boolean;
    isRelatedToRequired?: boolean;
    isLongMeasurementSection?: boolean;
    private disabledFunction?: DisabledFunction;
    showFieldLevelNAButton?: boolean;
    isNABlocked?: boolean;
    areAllValuesNA?: boolean;
    areAllValuesEmpty?: boolean;

    validators: IFieldValidator[] = null;
    jointValidationFieldsList: string[] = [];

    constructor(name: string, type: FieldType, measurementUnits: MeasurementUnits, parameters?: IParameters) {
        this.name = name;
        this.type = type;
        this.measurementUnits = measurementUnits || MeasurementUnits.NONE;
        this.validators = _.get(parameters, 'validators', []);
        this.isRequired = _.get(parameters, 'isRequired', false);
        this.isRelatedToRequired = _.get(parameters, 'isRelatedToRequired', false);
        this.section = _.get(parameters, 'section', 0);
        this.isLongMeasurementSection = _.get(parameters, 'isLongMeasurementSection', false);
        this.disabledFunction = _.get(parameters, 'disabledFunction', () => false);
        this.showFieldLevelNAButton = _.get(parameters, 'showFieldLevelNAButton', false);
        this.jointValidationFieldsList = _.get(parameters, 'jointValidationFieldsList', []);

        this.setValidatorsData();
    }

    /**
     * This method used in BaseMutableModel.getErrorMessagesForField and should be called
     * at least once for each model to set initial value for error list.
     * That is needed because information about errors are not received from server and
     * should be calculated by client.
     */
    validate(params: IFieldValidationParams): Array<string> {
        const { ignoreRequiredFields } = params;
        if (!_.get(this.validators, 'length', 0)) {
            return [];
        }
        const errorMessages = this.validators.reduce((result, validator) => {
            if (ignoreRequiredFields && validator instanceof RequiredFieldValidator) {
                return result;
            }
            const errorMessage = validator.validate(params);
            return errorMessage ? result.concat([errorMessage]) : result;
        }, []);
        return errorMessages;
    }

    isDisabled(model: any) {
        return this.disabledFunction(model);
    }

    private setValidatorsData() {
        this.validators.forEach(validator => {
            if (_.has(validator, 'propertyName')) {
                validator.propertyName = this.name;
            }
        });
    }

    detectGlobalChanges(table: IBaseTable, setDefaultFlags: boolean = false) {
        this.initNAProperties(table, setDefaultFlags);
    }

    initNAProperties(table: IBaseTable, setDefaultFlags: boolean = false) {
        /**
         * If no need to show N/A button there is no need to do extra calculations
         */
        if (!this.showFieldLevelNAButton || !table) {
            return;
        }

        const columns = table.table;

        this.areAllValuesNA = _.every(columns, tableCell => {
            return tableCell.value.currentState[this.name] === NA_VALUE;
        });

        this.areAllValuesEmpty = _.every(columns, tableCell => {
            return !_.isNumber(tableCell.value.currentState[this.name]);
        });

        if (setDefaultFlags) {
            this.isNABlocked = this.areAllValuesNA;
        }
    }

    setIsNABlocked(value: boolean) {
        this.isNABlocked = value;
    }
}
